import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CATEGORIES, formatDate } from "@/lib/constants";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = { title: "", subtitle: "", slug: "", cover_image: "", category: "news", author: "", body: "", published: true, published_at: new Date().toISOString(), attachments: [] };

export default function ArticlesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title || !editing.slug) return toast.error("Titolo e slug obbligatori");
    try {
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, editing);
      else await api.post("/admin/articles", editing);
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Eliminato"); load();
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((s) => ({ ...s, cover_image: data.path }));
    } catch { toast.error("Upload fallito"); }
    finally { setUploading(false); }
  };

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-3 py-2 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="admin-articles">
      <div className="flex items-end justify-between mb-6">
        <div><h1 className="font-display text-4xl font-medium">Articoli</h1><p className="text-sm text-[#292522]/60 mt-1">News, restituzioni e risorse.</p></div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus className="h-4 w-4" />Nuovo articolo</button>
      </div>

      <div className="rounded-2xl bg-white border border-[#292522]/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F1E8] text-xs uppercase"><tr><th className="text-left p-3">Titolo</th><th className="text-left p-3">Categoria</th><th className="text-left p-3">Data</th><th className="p-3"></th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-[#292522]/8">
                <td className="p-3"><div className="font-semibold">{a.title}</div><div className="text-xs text-[#292522]/50">/{a.slug}</div></td>
                <td className="p-3"><span className="rounded-full bg-[#FFF1E8] text-[#FF6100] font-semibold text-xs px-2 py-0.5">{a.category}</span></td>
                <td className="p-3 text-xs">{formatDate(a.published_at)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing({ ...empty, ...a })} className="p-1.5 hover:text-[#FF6100]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(a.id)} className="p-1.5 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#292522]/50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-[#292522]/8">
              <h2 className="font-display text-2xl font-medium">{editing.id ? "Modifica" : "Nuovo"} articolo</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <label><span className="text-xs font-semibold">Titolo *</span><input className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })} /></label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className="text-xs font-semibold">Slug</span><input className={input} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} /></label>
                <label><span className="text-xs font-semibold">Categoria</span>
                  <select className={input} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select>
                </label>
                <label><span className="text-xs font-semibold">Autore</span><input className={input} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></label>
                <label><span className="text-xs font-semibold">Sottotitolo</span><input className={input} value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></label>
              </div>
              <label><span className="text-xs font-semibold">Testo</span><textarea rows="8" className={input} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} /></label>
              <label className="block"><span className="text-xs font-semibold">Copertina</span>
                <input type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} disabled={uploading} className="text-xs" />
                {editing.cover_image && <p className="text-xs text-[#216B65] mt-1">✓ {editing.cover_image}</p>}
              </label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#FF6100]" /><span className="text-xs font-semibold">Pubblicato</span></label>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm">Annulla</button>
              <button onClick={save} className="btn-primary text-sm">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
